import React, { useEffect, useMemo, useState } from 'react';
import { DocumentUpload } from '../../../../../components/ui/DocumentUpload';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Plus, Upload, X } from 'lucide-react';
import { DocumentTag } from '../../../types';
import { AddMaterial, AddMaterialCategory, getPreSignedUrlToUpload, updateMaterialTraceability } from '../../../../../redux/Project/materialSaga';
import customNotification from '../../../../../components/ui/custom-notification';
import { MaterialData } from '../../../types/materials';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { Label } from '../../../../../components/ui/label';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../constants/fileUpload';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { FileUpload } from '../../../../../components/ui/FileUpload';

interface MaterialDocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: FileList | null) => void;
    title?: string;
    acceptedTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    buttonText?: string;
    label?: string;
    description?: string;
    required?: boolean;
    material?: MaterialData;
    documentTags: DocumentTag[];
    setDocumentTags: (tags: DocumentTag[]) => void;
    materialType?: string; // For customizing the modal based on material type
    selectedPipeSize?: any;
}

export const MaterialDocumentUploadModal: React.FC<MaterialDocumentUploadModalProps> = ({
    isOpen,
    onClose,
    onUpload,
    title = 'Upload Material Documents',
    acceptedTypes = '.pdf,.doc,.docx,.xlsx,.xls',
    multiple = false,
    maxFiles = 1,
    buttonText = 'Select Document',
    label = 'Document',
    description = 'Upload material documents (PDF, Excel, DOC, etc...)',
    required = true,
    material,
    documentTags,
    setDocumentTags,
    materialType,
    selectedPipeSize
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedTags, setSelectedTags] = useState<any[]>([]);
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
    const user = JSON.parse(localStorage.getItem('user') || '{}');


    // Local state for documentTags to ensure immediate updates
    const [localDocumentTags, setLocalDocumentTags] = useState<DocumentTag[]>(documentTags);

    const getTags = (category: any) => {
        if (category === 'Pipe') {

            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_TAGS');
        }
        else if (category === 'Forge') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_FORGE_TAGS');
        }
        else if (category === 'Gas') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_GAS_TAGS');
        }
        else if (category === 'Wire Lot') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_WIRE_LOT_TAGS');
        }
        else if (category === 'Coating') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_COATING_TAGS');
        }
        else if (category === 'Misc') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_MISC_TAGS');
        }
        else if (category === 'Pipe Grade') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_GRADE');
        }
        else if (category === 'Material Type') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_TYPE_TAGS');
        }
        else if (category === 'Manufacturer') {
            return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_MANUFACTURER_TAGS');
        }
        return [];
    }

    // Convert document tags to options for the MultiSelect component
    // value: string representation of tag ID, label: tag name
    const tagOptions = useMemo(() =>
        getTags(material?.materialCategory || 'Pipe').map(tag => ({
            value: tag.tags_id.toString(),
            label: tag.tags_name
        })), [localDocumentTags, material?.materialCategory]
    );

    // Convert document tags and certification tags to options for the MultiSelect component





    const handleFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setFiles(fileArray);
        }
    };

    const handleMaterialFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setMaterialDocuments(prev => [...prev, ...fileArray]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setMaterialDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const handleTagsChange = (fileIndex: number, tags: string[]) => {
        const updatedTags = [...selectedTags];
        updatedTags[fileIndex] = tags;
        setSelectedTags(updatedTags as any);
    };

    const handleUpload = async () => {
        if (materialDocuments && materialDocuments.length > 0 && material && selectedPipeSize) {
            setIsUploading(true);
            try {
                const payload = {
                    pipeId: Number(selectedPipeSize.pipeId),
                    projectId: Number(selectedPipeSize.projectId),
                    projectTypeId: Number(selectedPipeSize.projectTypeId),
                    materialCategory: material.materialCategory
                };

                const s3Response = await getPreSignedUrlToUpload(materialDocuments, payload, 1);

                if (s3Response && s3Response.length > 0) {
                    // Create new material document
                    const newMaterialDocument = {
                        materialId: material.materialId,
                        materialCategory: material.materialCategory,
                        materialStatus: material.materialStatus || "Available",
                        pipeId: material.pipeId,
                        assemblyNumbers: material.assemblyNumbers || [],
                        documents: s3Response.map((file: any, index: number) => ({
                            fileName: file.fileName,
                            s3Key: file.s3Key,
                            version: 1,
                            tags: selectedTags[index],
                            uploadedBy: user.email || ''
                        })),
                        details: material.details || {}
                    };

                    const apiPayload = {
                        data: [newMaterialDocument]

                    }
                    const response = await updateMaterialTraceability(apiPayload, material.materialCategory as string);

                    if (response) {
                        customNotification.success(
                            'Material document uploaded successfully',
                            `The ${material.materialCategory} document has been uploaded successfully.`
                        );

                        onUpload(materialDocuments as unknown as FileList);
                        setMaterialDocuments([]);
                        setIsUploading(false);
                        onClose();
                    }
                }
            } catch (error) {
                customNotification.error(
                    'Upload failed',
                    'There was an error uploading the material document. Please try again.'
                );
                setIsUploading(false);
            }
        }
    };

    const handleClose = () => {
        setFiles([]);
        setMaterialDocuments([]);
        setIsUploading(false);
        setSelectedTags([]);
        onClose();
    };



    // Sync local state with parent state when documentTags change
    useEffect(() => {
        setLocalDocumentTags(documentTags);
    }, [documentTags]);

    // Initialize local state when dialog opens
    useEffect(() => {
        if (isOpen) {
            setLocalDocumentTags(documentTags);
        }
    }, [isOpen, documentTags]);

    const handleAddTag = async (tagName: string) => {
        if (tagName.trim()) {

            if (getTags(material?.materialCategory || 'Pipe').find((tag: any) => tag.tags_name === tagName.trim())) {
                customNotification.warning(`${tagName.trim()} tag already exists`);

                return;
            }


            const apiPayload = {
                tags_name: tagName.trim(),
                project_id: Number(selectedPipeSize.projectId),
                tags_type: materialType === 'Pipe' ? 'MATERIAL_PIPE_TAGS' : materialType === 'Forge' ? 'MATERIAL_FORGE_TAGS' : materialType === 'Gas' ? 'MATERIAL_GAS_TAGS' : materialType === 'Wire Lot' ? 'MATERIAL_WIRE_LOT_TAGS' : 'MATERIAL_COATING_TAGS',
                created_by: user.email || ''
            }
            const res = await AddMaterialCategory(apiPayload);



            if (res) {

                // Update local state immediately for instant UI update
                const updatedLocalTags = [...documentTags, res];
                setLocalDocumentTags(updatedLocalTags);
                setDocumentTags(updatedLocalTags);
                // if (materialDocuments.length > 0) {
                //     let updatedTags = [...selectedTags];
                //     updatedTags[0] = [...updatedTags[0], res.tags_id.toString()];

                //     setSelectedTags(updatedTags as any);
                // }
                setIsAddingTag(false);
                // Automatically select the newly added tag
                // setSelectedTags(prev => [...prev, tagName.trim()]);
            }
        }
    };

    // Customize title based on material type
    const getModalTitle = () => {
        if (materialType) {
            return `Upload ${materialType} Material Document`;
        }
        return title;
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="w-[95vw] max-w-[1200px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white">
                <DialogHeader className="flex-shrink-0 border-b border-[#E2E5EA] px-6 py-4 pr-14">
                    <DialogTitle className="flex items-center gap-2 font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-hero-red-10">
                            <Upload className="h-5 w-5 text-hero-red" />
                        </span>
                        {getModalTitle()}
                    </DialogTitle>
                </DialogHeader>

                {/* Document Upload Section - Available for Pipe and Forge */}
                <div className="grid min-w-0 flex-1 grid-cols-4 gap-4 overflow-y-auto overflow-x-hidden p-6">
                    <div className="space-y-2 col-span-4">
                        <Label>Upload Documents</Label>
                        <p className="text-[13px] text-text-grey">
                            Upload the material documents.
                            <br />
                            <span className="text-[12px] text-heritage-grey">
                                Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                                <br />
                                Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL} per file
                            </span>
                        </p>

                        <FileUpload
                            onFileSelect={handleMaterialFileSelect}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                            multiple={true}
                            maxFiles={100}
                            buttonText="Upload Documents"
                            variant="outline"
                            size="lg"
                            onRemoveFile={handleRemoveFile}
                            selectedFiles={materialDocuments}
                            showFileList={materialDocuments.length > 0}
                            className="w-full"
                            showTags={true}
                            optionsTags={tagOptions}
                            selectedTags={selectedTags}
                            showAddButton={true}
                            addButtonText="Add New Tag"
                            onAddButtonClick={() => setIsAddingTag(true)}
                            onTagsChange={handleTagsChange}
                            maxHeight=""
                        />

                        {/* Debug: Show current documentFiles count */}
                        <div className="text-[12px] text-heritage-grey">
                            Current files: {materialDocuments.length} file(s)
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex flex-shrink-0 gap-2 border-t border-[#E2E5EA] bg-page-bg px-6 py-4">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        className="flex items-center gap-2"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!materialDocuments || materialDocuments.length === 0 || isUploading}
                        loading={isUploading}
                        loadingText="Uploading..."
                        className="flex items-center gap-2 bg-hero-red hover:bg-hero-red-60 text-white"
                    >
                        <Upload className="h-4 w-4" />
                        Upload
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Add New Tag Dialog */}
            <AddNewItemDialog
                open={isAddingTag}
                onOpenChange={setIsAddingTag}
                title="Add New Tag"
                label="Tag Name"
                placeholder="Enter tag name"
                onAdd={handleAddTag}
                buttonText="Add Tag"
                cancelText="Cancel"
            />
        </Dialog>
    );
}; 